import { updateComplianceItem, useBusinessEntity } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  AvatarGroup,
  Badge,
  Button,
  DataTable,
  Icons,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import type {
  CustomColumnDef,
  TableFilterType,
  TreeNode,
} from '@commenda/kakashi/src/components/DataTable/types';
import mixpanel, { trackAiEvent } from '@commenda/mixpanel';
import {
  ComplianceStatus,
  Country,
  ObligationRulesCategory,
} from '@commenda/prisma';
import type { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';
import {
  classNames,
  compareDatesChronologically,
  getRelativeDateDisplay,
  mapComplianceItemToUnifiedFiling,
  NonTimezoneSensitiveDate,
  useApp,
} from '@commenda/utils';
import { ComplianceModel } from '@commenda/utils/models/compliance';
import { SortingState } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from 'node_modules/@commenda/mixpanel';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SelectionActionBar } from '../../../components/SelectionActionBar';
import { useNotification } from '../../../hooks/useNotification';
import { FilingStatusBadge } from '../../../misc/FilingStatusBadge';
import { FilingCategoryBadge } from '../../../molecules/filings/FilingCategoryBadge';
import {
  BulkArchiveFilingsModal,
  BulkManageAssigneesModal,
} from '../../../molecules/filings/modals';
import { FilterConfig } from '../../../sales-tax/common/filters/types';
import { navigateToFilingDetail } from '../Calendar/calendarNavigation';
import { AddCustomFilingModal } from './AddCustomFilingModal';
import { ComplianceFilingDrawer } from './ComplianceFilingDrawer';
import { FilingDropdown } from './FilingDropdown';
import { InlineAssigneeCell } from './FilingsTableTnaCells';
import ExtendFilingModal from './modals/ExtendFilingModal';

const getDefaultFiltersConfig = (
  incorporationCountry: Country
): FilterConfig[] => [
  {
    id: 'type',
    type: 'SINGULAR',
    label: 'Type',
    placeholder: 'Select type',
    options: Object.values(ObligationRulesCategory).map((category) => {
      return {
        value: category,
        label: ComplianceModel.getFormattedComplianceCategory(
          category,
          incorporationCountry
        ),
      };
    }),
  },
  {
    id: 'assignedTo',
    type: 'MULTIPLE',
    label: 'Assigned to',
    placeholder: 'Select assigned to',
    options: [],
  },
  {
    id: 'period',
    type: 'SINGULAR',
    label: 'Period',
    placeholder: 'Select period',
    options: [],
  },
];

const parseVersionToStartEndYear = (version: string) => {
  if (!version) return null;

  const parts = version.trim().split(' ');
  if (parts.length !== 2) return null;
  const yearRange = parts[1];

  if (yearRange.includes('-')) {
    const [startYear, endYear] = yearRange.split('-');
    return {
      startYear: Number(startYear),
      endYear: Number(endYear),
    };
  }
  return {
    startYear: Number(yearRange),
    endYear: Number(yearRange),
  };
};

const FilingsTable = ({
  compliances,
  refreshCompliances,
  isAccountantAssignmentInProgress,
  showStatusColumn,
  filters,
  setFilters,
  incorporationCountry,
  loggedInUserEmail,
  loggedInUserId,
  businessEntityId,
  isTNAApp,
  showThunderIcon = true,
}: {
  compliances: UnifiedFiling[];
  refreshCompliances: () => void;
  isAccountantAssignmentInProgress: boolean;
  showStatusColumn: boolean;
  filters: Record<string, string | string[]>;
  setFilters: Dispatch<SetStateAction<Record<string, string | string[]>>>;
  incorporationCountry: Country;
  loggedInUserEmail: string;
  loggedInUserId: string;
  businessEntityId: number;
  showThunderIcon?: boolean;
  isTNAApp?: boolean;
}) => {
  const { appType } = useApp();
  const router = useRouter();

  // Filters are controlled via DataTable's filterConfig; we keep `filters` as the source of truth

  // create the options for the filters
  const allTypes = Array.from(
    new Set(compliances.map((compliance) => compliance.category))
  );
  const assigneeMap = new Map<string, string>();
  compliances.forEach((compliance) => {
    compliance.assignees.forEach((assignee) => {
      if (assignee.id && assignee.name) {
        assigneeMap.set(assignee.id, assignee.name);
      }
    });
    if (
      compliance.assignedAccountant?.id &&
      compliance.assignedAccountant?.name
    ) {
      assigneeMap.set(
        compliance.assignedAccountant.id,
        compliance.assignedAccountant.name
      );
    }
  });

  const allAssigneeData = Array.from(assigneeMap.entries()).map(
    ([id, name]) => ({ id, name })
  );

  const fiscalYearRanges = Array.from(
    new Set(
      compliances
        .filter((filing) => {
          const { version } = filing;
          const yearRange = version.split(' ')[1];
          if (yearRange.includes('-')) {
            const [startYear, endYear] = yearRange.split('-');
            // only allowing filters for 1 or 0 year ranges
            if (Number(endYear) > Number(startYear) + 1) {
              return false;
            }
          }
          return true;
        })
        .map((filing) => {
          const { version } = filing;
          const yearRange = version.split(' ')[1];
          return yearRange;
        })
    )
  ).map((yearRange) => {
    return {
      value: yearRange,
      label: `FY ${yearRange}`,
    };
  });

  const filtersConfig = [...getDefaultFiltersConfig(incorporationCountry)];
  // add the options to the filter config
  filtersConfig[0].options = allTypes.map((type) => {
    const label = ComplianceModel.getFormattedComplianceCategory(
      type,
      incorporationCountry
    );
    return { value: type, label };
  });
  filtersConfig[1].options = allAssigneeData.map((assignee) => ({
    value: assignee.id,
    label: assignee.name,
  }));
  filtersConfig[2].options = fiscalYearRanges;

  // execute the filters
  const filteredCompliances = compliances.filter((compliance) => {
    const { type, assignedTo, period } = filters;

    // Type filter
    if (type && compliance.category !== type) {
      return false;
    }

    // AssignedTo filter
    if (assignedTo) {
      const selectedAssigneeIds = Array.isArray(assignedTo)
        ? assignedTo
        : [assignedTo];
      if (selectedAssigneeIds.length > 0) {
        const complianceAssigneeIds = [
          ...compliance.assignees.map((assignee) => assignee.id),
          ...(compliance.assignedAccountant
            ? [compliance.assignedAccountant.id]
            : []),
        ].filter((id) => id != null);

        if (
          !selectedAssigneeIds.some((selectedId) =>
            complianceAssigneeIds.includes(selectedId)
          )
        ) {
          return false;
        }
      }
    }

    if (period) {
      const versionInfo = parseVersionToStartEndYear(compliance.version);

      if (period && versionInfo) {
        // period is a string like "2024-2025"
        if (typeof period !== 'string') return true;
        const allYears = period.split('-').map(Number);
        // check if the versionInfo is within the range of the years
        if (versionInfo.startYear === versionInfo.endYear) {
          if (!allYears.includes(versionInfo.startYear)) {
            return false;
          }
        } else if (
          !allYears.includes(versionInfo.startYear) ||
          !allYears.includes(versionInfo.endYear)
        ) {
          return false;
        }
      }
    }

    // If all checks pass, include the compliance item
    return true;
  });

  const isArchivedView = useMemo(() => {
    if (filteredCompliances.length === 0) return false;
    return filteredCompliances.every(
      (c) => c.status === ComplianceStatus.ARCHIVED
    );
  }, [filteredCompliances]);

  const isCompletedView = useMemo(() => {
    if (filteredCompliances.length === 0) return false;
    return filteredCompliances.every(
      (c) => c.status === ComplianceStatus.COMPLETED
    );
  }, [filteredCompliances]);

  const isReadOnlyView = isArchivedView || isCompletedView; // to avoid bulk actions on archived/completed tabs - archiving archived items doesn't make sense

  const [editingFiling, setEditingFiling] = useState<UnifiedFiling | null>(
    null
  );
  const editFilingModalVisibility = useToggle(false);
  // Modal needs companyId; derived from the entity since callers don't pass it.
  const { data: businessEntityForModal } = useBusinessEntity(businessEntityId);
  // companyId for the Tax & Reporting inline assignee picker (same derivation as the modal).
  const tnaCompanyId =
    businessEntityForModal?.generalCorporationInfo?.companyId;

  // The compliance-calendar table redesign (column reorder, neutral Type pill, inline assignee
  // picker, confirm/deny quick actions, accent bar, client-managed side panel) is gated behind
  // the obligation-rules-generator launch. Flag OFF → the original Monitor table.
  const redesignEnabled = isFeatureLaunched(
    FeatureLaunch.ObligationRulesGenerator,
    tnaCompanyId
  );

  // Due-date column, extracted so the Tax & Reporting variant can reorder it (it sits right
  // after Type there, vs. last in the default Monitor layout) without duplicating the cell.
  const dueDateColumn: CustomColumnDef<UnifiedFiling, unknown> = {
    id: 'dueDate',
    accessorKey: 'dueDate',
    accessorFn: null,
    header: 'Due date',
    sort: {
      shouldSort: true,
      sortingFn: compareDatesChronologically,
      ui: {
        asc: {
          columnNode: 'Oldest to Newest',
          globalNode: 'Due date - Oldest to Newest',
        },
        desc: {
          columnNode: 'Newest to Oldest',
          globalNode: 'Due date - Newest to Oldest',
        },
      },
    },
    cell: ({ row }) => {
      const formattedDueDate = getRelativeDateDisplay(row.original.dueDate);

      if (row.original.status === ComplianceStatus.COMPLETED) {
        const onlyDate = NonTimezoneSensitiveDate.fromISO8601String(
          row.original.dueDate
        ).format('MMM DD, YYYY');
        return (
          <span className="txt-small-medium text-text-primary">{onlyDate}</span>
        );
      }

      return row.original.dueDate ? (
        <span
          className={classNames(
            'txt-small-medium',
            formattedDueDate === 'Today' || formattedDueDate === 'Tomorrow'
              ? 'text-fg-error'
              : 'text-text-primary'
          )}
        >
          {formattedDueDate}
        </span>
      ) : null;
    },
  };
  const bulkArchiveModalVisibility = useToggle(false);
  const bulkManageAssigneesModalVisibility = useToggle(false);
  const { setNotification } = useNotification();

  // Bulk assignee/archive actions only support direct-tax compliance items;
  // indirect-tax (sales-tax) filings must be deselected first.
  const showIndirectTaxNotSupported = () =>
    setNotification({
      show: true,
      valid: false,
      title: 'Not supported for indirect tax filings',
      message:
        'Manage assignees and archive are not supported for indirect tax filings. Please deselect them and try again.',
    });
  const [selectedRows, setSelectedRows] = useState<UnifiedFiling[]>([]);
  // Side panel for a client-managed compliance filing (opened from a row click).
  const [drawerFiling, setDrawerFiling] = useState<UnifiedFiling | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  // Grouping: both the redesign and the original Monitor table default to grouping by type
  // with the toolbar enabled.
  const [selectedGroupingOption, setSelectedGroupingOption] = useState<
    string | null
  >('type');
  // Default to sort by due date ascending on initial load
  const [sortingState, setSortingState] = useState<SortingState>([
    { id: 'dueDate', desc: false },
  ]);

  // The panel has no route of its own, so mirror the open filing into ?filingId= — that is what
  // makes a filing linkable from outside the table (the agent hands these links out) and what lets
  // the browser's back button close the panel.
  const setFilingIdParam = (filingId: string | null) => {
    const { filingId: current, ...rest } = router.query;
    if (current === (filingId ?? undefined)) return;
    router.replace(
      {
        pathname: router.pathname,
        query: filingId ? { ...rest, filingId } : rest,
      },
      undefined,
      { shallow: true }
    );
  };

  const openDrawerFiling = (filing: UnifiedFiling | null) => {
    setDrawerFiling(filing);
    setFilingIdParam(filing?.id ?? null);
  };

  // Client-managed compliance filings open the side panel
  // for assignee + acknowledgement upload, instead of navigating to the SR page.
  // Redesign-only — the original table always navigates to the filing detail.
  const openFiling = (filing: UnifiedFiling) => {
    if (
      redesignEnabled &&
      filing.source === 'COMPLIANCE_ITEM' &&
      filing._complianceItem?.managedBy === 'CLIENT'
    ) {
      openDrawerFiling(filing);
      return;
    }

    navigateToFilingDetail(filing, router);
  };

  // A link can name a filing (?filingId=) — open it exactly as clicking its row would, once the
  // rows it belongs to have loaded.
  const openedFromQuery = useRef<string | null>(null);
  useEffect(() => {
    const { filingId } = router.query;
    if (typeof filingId !== 'string') {
      openedFromQuery.current = null;
      return;
    }
    if (openedFromQuery.current === filingId) return;

    const filing = compliances.find((row) => row.id === filingId);
    if (!filing) return;

    openedFromQuery.current = filingId;
    openFiling(filing);
  }, [router.query.filingId, compliances]);

  // When grouping by type, pre-sort by category order to ensure groups appear in the correct order
  // The table will handle sorting within groups (by due date) via tableSortConfig
  const sortedCompliances = useMemo(() => {
    if (selectedGroupingOption === 'type') {
      // Define category order for grouping
      const categoryOrder: Partial<Record<ObligationRulesCategory, number>> = {
        [ObligationRulesCategory.DIRECT_TAX]: 1,
        [ObligationRulesCategory.INDIRECT_TAX]: 2,
        [ObligationRulesCategory.TRANSFER_PRICING]: 3,
        [ObligationRulesCategory.ENTITY_COMPLIANCE]: 4,
        [ObligationRulesCategory.PAYROLL_AND_EMPLOYMENT]: 5,
        [ObligationRulesCategory.CUSTOMS_AND_TRADE]: 6,
      };
      // Only pre-sort by category order when grouping by type
      return [...filteredCompliances].sort((a, b) => {
        const categoryOrderA = categoryOrder[a.category] ?? 999;
        const categoryOrderB = categoryOrder[b.category] ?? 999;
        return categoryOrderA - categoryOrderB;
      });
    }
    // When not grouping by type, don't pre-sort - let the table handle it
    return filteredCompliances;
  }, [filteredCompliances, selectedGroupingOption]);

  useEffect(() => {
    setSelectedRows([]);
    setSelectedRowIds(new Set());
  }, [compliances]);

  const handleEditFiling = (filing: UnifiedFiling) => {
    setEditingFiling(filing);
    editFilingModalVisibility.show();
  };

  const handleCloseEditModal = () => {
    editFilingModalVisibility.hide();
    setEditingFiling(null);
  };

  const [extendingFiling, setExtendingFiling] =
    useState<TComplianceItem | null>(null);
  const extendFilingModalVisibility = useToggle(false);

  const handleExtendFiling = (filing: TComplianceItem) => {
    setExtendingFiling(filing);
    extendFilingModalVisibility.show();
  };

  const handleCloseExtendModal = () => {
    extendFilingModalVisibility.hide();
    setExtendingFiling(null);
  };

  // Grouping configuration
  const groupingConfig = useMemo(() => {
    const getAssigneeGroupKey = (compliance: UnifiedFiling): string => {
      if (compliance.assignees.length > 0 && compliance.assignees[0]?.name) {
        return compliance.assignees[0].name;
      }
      if (compliance.assignedAccountant?.name) {
        return compliance.assignedAccountant.name;
      }
      return 'Unassigned';
    };

    const renderAssigneeGroupHeader = (
      groupKey: string,
      rows: TreeNode<UnifiedFiling>[]
    ) => {
      const count = rows.length;
      return (
        <div className="flex items-center gap-md">
          <span className="txt-small-medium text-text-secondary">
            {groupKey}
          </span>
          <span className="txt-small text-text-quaternary">({count})</span>
        </div>
      );
    };

    const renderTypeGroupHeader = (
      groupKey: string,
      rows: TreeNode<UnifiedFiling>[]
    ) => {
      const count = rows.length;
      const category = groupKey as ObligationRulesCategory;
      return (
        <div className="flex items-center gap-md">
          <FilingCategoryBadge
            category={category}
            country={incorporationCountry}
            size="sm"
          />
          <span className="txt-small text-text-quaternary">({count})</span>
        </div>
      );
    };

    const renderStatusGroupHeader = (
      groupKey: string,
      rows: TreeNode<UnifiedFiling>[]
    ) => {
      const count = rows.length;
      const firstRow = rows[0] as unknown as UnifiedFiling;
      return (
        <div className="flex items-center gap-md">
          <FilingStatusBadge
            status={firstRow.status}
            syntheticStatus={firstRow.syntheticStatus}
          />
          <span className="txt-small text-text-quaternary">({count})</span>
        </div>
      );
    };

    return {
      isEnabled: !!selectedGroupingOption,
      getGroupKey: (row: UnifiedFiling) => {
        // This won't be used when toolbar is enabled, but required by type
        return row.category;
      },
      renderGroupHeader: (groupKey: string, rows: UnifiedFiling[]) => {
        // This won't be used when toolbar is enabled, but required by type
        return <div>{groupKey}</div>;
      },
      toolbar: {
        // Grouping toolbar is enabled in both the redesign and the original table.
        isEnabled: true,
        value: selectedGroupingOption,
        onChange: setSelectedGroupingOption,
        options: [
          {
            id: 'type',
            label: 'Type',
            getGroupKey: (row: TreeNode<UnifiedFiling>) => {
              const compliance = row as unknown as UnifiedFiling;
              return compliance.category || 'Unknown';
            },
            renderGroupHeader: renderTypeGroupHeader,
          },
          {
            id: 'status',
            label: 'Status',
            getGroupKey: (row: TreeNode<UnifiedFiling>) => {
              const compliance = row as unknown as UnifiedFiling;
              return String(
                compliance.syntheticStatus || compliance.status || 'Unknown'
              );
            },
            renderGroupHeader: renderStatusGroupHeader,
          },
          {
            id: 'assignedTo',
            label: 'Assigned to',
            getGroupKey: (row: TreeNode<UnifiedFiling>) => {
              const compliance = row as unknown as UnifiedFiling;
              return getAssigneeGroupKey(compliance);
            },
            renderGroupHeader: renderAssigneeGroupHeader,
          },
        ],
      },
    };
  }, [selectedGroupingOption, incorporationCountry]);

  return (
    <div className="flex flex-col gap-xl" data-ai-section="Filings">
      <DataTable
        rowDataAttributes={(row) => ({
          'data-ai-item': 'Filing',
          ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
        })}
        onRowClick={(row) => {
          if (isTNAApp)
            mixpanel.track(MIXPANEL_EVENTS.TNA_FILING_CLICKED, {
              surface_area: MIXPANEL_SURFACE_AREA.TA_APP,
              task_id: row.id,
            });

          trackAiEvent('filing_engaged', {
            filing_domain:
              row.source === 'SALES_TAX_FILING' ? 'indirect_tax' : 'compliance',
            surface: isTNAApp
              ? MIXPANEL_SURFACE_AREA.TA_APP
              : MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
            filing_id: row.id,
          });

          openFiling(row);
        }}
        filterConfig={{
          filters: [
            {
              columnId: 'type',
              label: 'Type',
              icon: <Icons.BracketsCheck className="size-4" />,
              type: 'SINGLE_SELECT' as TableFilterType,
              options: filtersConfig[0].options.map((option) => ({
                label: option.label,
                value: option.value as string,
              })),
            },
            {
              columnId: 'assignedTo',
              label: 'Assigned to',
              icon: <Icons.User01 className="size-4" />,
              type: 'MULTI_SELECT' as TableFilterType,
              showSearch: true,
              placeholder: 'Search assignees',
              options: filtersConfig[1].options.map((option) => ({
                label: option.label,
                value: option.value as string,
              })),
            },
            {
              columnId: 'period',
              label: 'Period',
              icon: <Icons.Clock className="size-4" />,
              type: 'SINGLE_SELECT' as TableFilterType,
              options: filtersConfig[2].options.map((option) => ({
                label: option.label,
                value: option.value as string,
              })),
            },
          ],
          activeFilters: filters as Record<string, string | string[]>,
          onFilterChange: (activeFilters) => {
            setFilters(activeFilters);
          },
        }}
        columnGroups={[
          {
            id: 'compliances-title',
            sticky: 'left',
            columns: [
              {
                id: 'title',
                accessorKey: 'title',
                accessorFn: (row) => row.title,
                header: 'Filing',
                width: 300,
                cell: ({ row }) => {
                  const isPayingForCompliance = !!row.original.hasPaidForFiling;
                  const { title } = row.original;
                  // Highlight the row's left edge while a rule-engine "needs confirmation"
                  // item is still awaiting a decision (redesign-only design cue).
                  const needsConfirmation =
                    redesignEnabled &&
                    row.original.mode === 'GENERATE_AS_CONFIRMATION';

                  return (
                    <>
                      {needsConfirmation && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-0 bottom-0 w-[3px] bg-utility-warning-400"
                        />
                      )}
                      <p className="txt-small-medium flex items-center gap-lg min-w-0">
                        <span className="min-w-0 flex-1 truncate">{title}</span>
                        {showThunderIcon && isPayingForCompliance && (
                          <Tooltip
                            content="Filing through Commenda"
                            side="bottom"
                          >
                            <Icons.Zap className="size-4 text-fg-quaternary-hover shrink-0" />
                          </Tooltip>
                        )}
                      </p>
                    </>
                  );
                },
              },
            ],
          },
          {
            id: 'compliances-main',
            columns: [
              // Hide the Type column when grouping by type — the group header already shows the
              // category, so a per-row Type badge would be redundant.
              ...(selectedGroupingOption === 'type'
                ? []
                : [
                    {
                      id: 'category',
                      accessorKey: 'category',
                      accessorFn: (row) => row.category,
                      header: 'Type',
                      cell: ({ row }) =>
                        redesignEnabled ? (
                          // Neutral pill — the Type column is not color-coded (redesign).
                          <Badge variant="gray" size="sm">
                            {ComplianceModel.getFormattedComplianceCategory(
                              row.original.category,
                              incorporationCountry
                            )}
                          </Badge>
                        ) : (
                          // Original color-coded category badge.
                          <FilingCategoryBadge
                            category={row.original.category}
                            country={incorporationCountry}
                            size="sm"
                          />
                        ),
                    } as CustomColumnDef<UnifiedFiling, unknown>,
                  ]),
              // Redesign: Due date sits right after Type. Original: it's the last column (added below).
              ...(redesignEnabled ? [dueDateColumn] : []),
              {
                id: 'filingPeriod',
                accessorKey: 'filingPeriod',
                accessorFn: null,
                header: 'Period',
                cell: ({ row }) => (
                  <span className="txt-small-regular text-text-primary">
                    {row.original.version}
                  </span>
                ),
              },
              {
                id: 'assignedTo',
                accessorKey: 'assignedTo',
                accessorFn: null,
                header: 'Assigned to',
                cell: ({ row }) => {
                  // Inline editable assignee picker (redesign-only).
                  if (
                    redesignEnabled &&
                    row.original.source === 'COMPLIANCE_ITEM'
                  ) {
                    return (
                      <div className="py-1 w-full">
                        <InlineAssigneeCell
                          filing={row.original}
                          companyId={tnaCompanyId}
                          refreshCompliances={refreshCompliances}
                        />
                      </div>
                    );
                  }

                  const showAssigningInProgress =
                    row.original.source === 'COMPLIANCE_ITEM' &&
                    !row.original.isCustom &&
                    isAccountantAssignmentInProgress;

                  if (showAssigningInProgress) {
                    return (
                      <span className="text-sm text-text-disabled italic">
                        Not yet assigned
                      </span>
                    );
                  }

                  const avatars = [
                    ...row.original.assignees.map((assignee) => ({
                      name: assignee.name,
                      tooltip: {
                        content: assignee.name,
                        side: 'top',
                      },
                    })),
                    ...(row.original.assignedAccountant
                      ? [
                          {
                            name: row.original.assignedAccountant.name,
                            tooltip: {
                              content: row.original.assignedAccountant.name,
                              side: 'top',
                            },
                          },
                        ]
                      : []),
                  ];

                  if (avatars.length === 0) {
                    return (
                      <span className="txt-small-regular text-text-quaternary">
                        N/A
                      </span>
                    );
                  }

                  return <AvatarGroup avatars={avatars} size="xs" />;
                },
              },
              ...(showStatusColumn
                ? [
                    {
                      id: 'status',
                      accessorKey: 'status',
                      accessorFn: null,
                      header: 'Status',
                      cell: ({ row }) => {
                        return (
                          <FilingStatusBadge
                            status={row.original.status}
                            syntheticStatus={row.original.syntheticStatus}
                          />
                        );
                      },
                    },
                  ]
                : []),
              // Original layout keeps Due date as the last column (redesign moves it after Type).
              ...(redesignEnabled ? [] : [dueDateColumn]),
            ],
          },
          {
            id: 'compliances-actions',
            sticky: 'right',
            columns: [
              {
                id: 'actions',
                accessorKey: 'actions',
                accessorFn: null,
                header: '',
                width: 64,
                cell: ({ row }) => {
                  if (row.original.source === 'SALES_TAX_FILING') return null;
                  const complianceItem = row.original._complianceItem;
                  if (!complianceItem) return null;

                  return (
                    <FilingDropdown
                      filing={complianceItem}
                      refreshCompliances={refreshCompliances}
                      loggedInUserEmail={loggedInUserEmail}
                      onEditFiling={(f) =>
                        handleEditFiling(mapComplianceItemToUnifiedFiling(f))
                      }
                      onExtendFiling={handleExtendFiling}
                    />
                  );
                },
              },
            ],
          },
        ]}
        searchConfig={{
          isEnabled: true,
          columnsToEnableSearchFor: ['title'],
          placeholder: 'Search filing name',
        }}
        emptyStateConfig={{
          title: 'No filings found',
          description: "You don't have any filings here yet.",
        }}
        data={sortedCompliances}
        paginationConfig={{
          isEnabled: !selectedGroupingOption,
          pageSize: 10,
          type: 'PAGE',
        }}
        rowSelectionConfig={{
          isEnabled: !isReadOnlyView,
          selectionMode: 'multiple',
          onSelectionChange: (rows) => {
            const typedRows = rows as UnifiedFiling[];
            setSelectedRows(typedRows);
            const nextIds = new Set<string>();
            typedRows.forEach((r) => nextIds.add(r.id));
            setSelectedRowIds(nextIds);
          },
          getRowId: (row) => (row as UnifiedFiling).id,
          isRowSelected: (row) => {
            const { id } = row as UnifiedFiling;
            return selectedRowIds.has(id);
          },
          shouldShowRowSelectActionMenu: false,
        }}
        tableViewConfig={{
          isEnabled: true,
          defaultView: 'regular',
        }}
        columnSelectorConfig={{
          isEnabled: true,
        }}
        groupingConfig={groupingConfig}
        tableSortConfig={{
          sortingState,
          onSortingChange: (newSorting) => {
            setSortingState(newSorting);
          },
        }}
      />

      <SelectionActionBar
        count={isReadOnlyView ? 0 : selectedRows.length}
        itemNoun="filing"
        onClear={() => {
          setSelectedRows([]);
          setSelectedRowIds(new Set());
        }}
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (selectedRows.some((r) => r.source === 'SALES_TAX_FILING')) {
              showIndirectTaxNotSupported();
            } else {
              bulkManageAssigneesModalVisibility.show();
            }
          }}
        >
          <Icons.User01 className="size-4" />
          <span className="txt-small-medium">Manage assignees</span>
        </Button>
        <Button
          size="sm"
          variant="destructive-secondary"
          onClick={() => {
            if (selectedRows.some((r) => r.source === 'SALES_TAX_FILING')) {
              showIndirectTaxNotSupported();
            } else {
              bulkArchiveModalVisibility.show();
            }
          }}
        >
          <Icons.Archive className="size-4" />
          <span className="txt-small-medium">Archive</span>
        </Button>
      </SelectionActionBar>

      {editingFiling &&
        editingFiling._complianceItem &&
        businessEntityForModal && (
          <AddCustomFilingModal
            isOpen={editFilingModalVisibility.visible}
            onClose={handleCloseEditModal}
            companyId={businessEntityForModal.generalCorporationInfo.companyId}
            businessEntityId={businessEntityId}
            refreshCompliances={refreshCompliances}
            editingFiling={editingFiling._complianceItem}
            loggedInUserId={loggedInUserId}
          />
        )}

      {extendingFiling && (
        <ExtendFilingModal
          isOpen={extendFilingModalVisibility.visible}
          onClose={handleCloseExtendModal}
          filingId={extendingFiling.id}
          filingTitle={extendingFiling.title}
          currentDueDate={extendingFiling.dueDate}
          entityName={extendingFiling.corporation?.companyDisplayName}
          companyId={extendingFiling.company.id}
          onSuccess={refreshCompliances}
        />
      )}

      <BulkArchiveFilingsModal
        isOpen={bulkArchiveModalVisibility.visible}
        onClose={bulkArchiveModalVisibility.hide}
        selectedCount={
          selectedRows.filter((r) => r.source === 'COMPLIANCE_ITEM').length
        }
        onArchiveMany={async (reason) => {
          try {
            const complianceRows = selectedRows.filter(
              (r) => r.source === 'COMPLIANCE_ITEM'
            );
            await Promise.all(
              complianceRows.map((row) =>
                updateComplianceItem(row.id, {
                  status: ComplianceStatus.ARCHIVED,
                  reasonToArchive: reason,
                })
              )
            );
            return true;
          } catch {
            return false;
          }
        }}
        onSuccess={() => {
          setSelectedRows([]);
          setSelectedRowIds(new Set());
          refreshCompliances();
        }}
      />

      <BulkManageAssigneesModal
        isOpen={bulkManageAssigneesModalVisibility.visible}
        onClose={bulkManageAssigneesModalVisibility.hide}
        selectedFilings={selectedRows
          .filter((r) => r.source === 'COMPLIANCE_ITEM' && r._complianceItem)
          .map((r) => r._complianceItem!)}
        onSuccess={() => {
          setSelectedRows([]);
          setSelectedRowIds(new Set());
          refreshCompliances();
        }}
      />
      <ComplianceFilingDrawer
        filing={drawerFiling}
        companyId={tnaCompanyId}
        incorporationCountry={incorporationCountry}
        refreshCompliances={refreshCompliances}
        onClose={() => openDrawerFiling(null)}
      />
    </div>
  );
};

export { FilingsTable };
